<template>
  <v-container fluid class="py-2">
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="0" class="pg-main-card tw-mt-2 md:tw-mt-4">
          <div class="pg-header">
            <div class="pg-heading">
              <div class="pg-badge">
                <v-icon color="white">mdi-car-windshield</v-icon>
              </div>
              <div>
                <h2 class="pg-title">Windscreen Fitters</h2>
                <p class="pg-subtitle">
                  Manage approved windscreen fitting service providers.
                </p>
              </div>
            </div>
            <v-btn
              text
              color="primary"
              class="pg-refresh"
              :loading="loadingTable"
              @click="fetchFitters()"
            >
              <v-icon left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>

          <v-divider class="mb-3" />

          <div class="pg-table">
            <v-data-table
              :headers="headers"
              :items="fitters"
              :loading="loadingTable"
              :items-per-page="-1"
              disable-pagination
              hide-default-footer
              class="elevation-1"
              loading-text="Loading windscreen fitters..."
              no-data-text="No active windscreen fitters found"
              @click:row="openDetailPanel"
            >
              <template v-slot:[`item.location.address`]="{ item: provider }">
                <span>{{ locationValue(provider, 'address') }}</span>
              </template>

              <template v-slot:[`item.location.region`]="{ item }">
                {{ locationValue(item, 'region') }}
              </template>

              <template v-slot:[`item.phoneNumber`]="{ item }">
                <span class="text-no-wrap">{{ item.phoneNumber || '—' }}</span>
              </template>

              <template v-slot:[`item.email`]="{ item }">
                <span class="text-no-wrap">{{ item.email || '—' }}</span>
              </template>

              <template v-slot:[`item.active`]="{ item }">
                <v-chip
                  small
                  :color="item.active ? 'success' : 'grey'"
                  text-color="white"
                >
                  <span>
                    {{ item.active ? 'Active' : 'Inactive' }}
                  </span>
                </v-chip>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-cell">
                  <v-btn
                    icon
                    small
                    color="secondary"
                    :aria-label="`Edit ${item.name}`"
                    @click.stop="openEditDialog(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :color="item.active ? 'error' : 'success'"
                    :loading="statusChangingIds.includes(item.id)"
                    :disabled="statusChangingIds.includes(item.id)"
                    :aria-label="`${item.active ? 'Disable' : 'Activate'} ${
                      item.name
                    }`"
                    @click.stop="openStatusDialog(item, !item.active)"
                  >
                    <v-icon small>
                      {{
                        item.active ? 'mdi-cancel' : 'mdi-check-circle-outline'
                      }}
                    </v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <div class="pg-pagination">
              <div class="pagination-size">
                <span>Rows per page:</span>
                <v-select
                  v-model="limit"
                  :items="pageSizes"
                  dense
                  hide-details
                  class="page-size-select"
                  aria-label="Rows per page"
                  @change="changePageSize"
                />
              </div>
              <span class="pagination-range">{{ showingLabel }}</span>
              <v-btn
                icon
                small
                aria-label="Previous page"
                :disabled="loadingTable || pagination.previousOffset === null"
                @click="goToOffset(pagination.previousOffset)"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                aria-label="Next page"
                :disabled="loadingTable || !pagination.hasMore"
                @click="goToOffset(pagination.nextOffset)"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-btn
      bottom
      class="secondary add-button"
      dark
      fab
      fixed
      right
      aria-label="Add windscreen fitter"
      @click="openCreateDialog"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <!-- Centered Fitter Details Pop-up Modal -->
    <v-dialog v-model="detailDialog" max-width="600px" persistent>
      <v-card
        class="dlg-card"
        v-if="selectedFitter"
        style="border-radius: 12px; overflow: hidden"
      >
        <!-- Refined custom dialog header with soft blue badge -->
        <div
          class="px-6 py-4 d-flex align-center justify-space-between"
          style="border-bottom: 1px solid #eef2f8"
        >
          <div class="d-flex align-center">
            <div
              class="d-flex align-center justify-center mr-3"
              style="
                background-color: #e8f0fe;
                color: #1565c0;
                border-radius: 8px;
                padding: 8px;
                width: 40px;
                height: 40px;
              "
            >
              <v-icon color="#1565c0">mdi-car-windshield</v-icon>
            </div>
            <div>
              <div
                class="text-h6 font-weight-bold text-secondary"
                style="line-height: 1.2"
              >
                Fitter Details
              </div>
              <div class="text-caption grey--text">
                Approved windscreen fitter information
              </div>
            </div>
          </div>
          <v-btn icon aria-label="Close details" @click="closeDetailPanel">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <!-- Modal Content Details (Refined Two-Column Inline Layout) -->
        <v-card-text class="py-2 detail-text">
          <v-container class="py-0">
            <!-- ID -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-card-account-details-outline</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  ID
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ selectedFitter.id || '—' }}
              </v-col>
            </v-row>

            <!-- Company Name -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-domain</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  COMPANY NAME
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ selectedFitter.name }}
              </v-col>
            </v-row>

            <!-- Address -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-map-marker</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  ADDRESS
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ locationValue(selectedFitter, 'address') }}
              </v-col>
            </v-row>

            <!-- City -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-city</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  CITY
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ locationValue(selectedFitter, 'city') }}
              </v-col>
            </v-row>

            <!-- Region -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-map</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  REGION
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ locationValue(selectedFitter, 'region') }}
              </v-col>
            </v-row>

            <!-- Phone No. -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-phone</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  PHONE NO.
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ selectedFitter.phoneNumber || '—' }}
              </v-col>
            </v-row>

            <!-- Company Email -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-email</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  COMPANY EMAIL
                </span>
              </v-col>
              <v-col
                cols="7"
                class="text-body-2 font-weight-medium text--primary text-left"
              >
                {{ selectedFitter.email || '—' }}
              </v-col>
            </v-row>

            <!-- Status -->
            <v-row
              align="center"
              no-gutters
              class="py-2"
              style="border-bottom: 1px solid #eef2f8"
            >
              <v-col cols="5" class="d-flex align-center">
                <v-icon small color="grey darken-1" class="mr-2"
                  >mdi-check-circle-outline</v-icon
                >
                <span
                  class="grey--text text--darken-1 font-weight-bold text-caption text-uppercase"
                >
                  STATUS
                </span>
              </v-col>
              <v-col cols="7" class="text-left">
                <v-chip
                  small
                  :color="selectedFitter.active ? 'success' : 'grey'"
                  text-color="white"
                  class="font-weight-bold"
                >
                  {{ selectedFitter.active ? 'Active' : 'Inactive' }}
                </v-chip>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <!-- Modal Actions -->
        <v-card-actions
          class="dlg-actions px-6 py-4 d-flex justify-end grey lighten-4"
        >
          <v-btn
            outlined
            color="secondary"
            class="mr-3"
            aria-label="Edit fitter from details modal"
            @click="editFromDrawer(selectedFitter)"
          >
            Edit
          </v-btn>
          <v-btn
            depressed
            :color="selectedFitter.active ? 'error' : 'success'"
            aria-label="Change status from details modal"
            @click="statusFromDrawer(selectedFitter)"
          >
            {{ selectedFitter.active ? 'Disable' : 'Activate' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog Form -->
    <v-dialog v-model="formDialog" max-width="900px" persistent>
      <v-card class="dlg-card">
        <v-form
          ref="fitterForm"
          v-model="formValid"
          @submit.prevent="saveFitter"
        >
          <dialog-header
            :title="formTitle"
            icon="mdi-car-windshield"
            :subtitle="formSubtitle"
            @close="closeFormDialog"
          />
          <v-divider />
          <v-card-text class="dlg-body">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.name"
                    label="Company Name"
                    prepend-icon="mdi-store-outline"
                    :rules="[requiredRule('Company name')]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.email"
                    label="Company Email"
                    prepend-icon="mdi-email-outline"
                    :rules="[requiredRule('Email address'), emailRule]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.phoneNumber"
                    label="Phone Number"
                    prepend-icon="mdi-phone-outline"
                    :rules="[requiredRule('Phone number')]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.location.address"
                    label="Address"
                    prepend-icon="mdi-map-marker-outline"
                    :rules="[requiredRule('Location address')]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.location.city"
                    label="City"
                    prepend-icon="mdi-city-variant-outline"
                    :rules="[requiredRule('Location city')]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="form.location.region"
                    label="Region"
                    prepend-icon="mdi-map-outline"
                    :rules="[requiredRule('Location region')]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.location.latitude"
                    label="Latitude"
                    prepend-icon="mdi-latitude"
                    type="number"
                    step="any"
                    :rules="[requiredRule('Location latitude'), latitudeRule]"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.location.longitude"
                    label="Longitude"
                    prepend-icon="mdi-longitude"
                    type="number"
                    step="any"
                    :rules="[requiredRule('Location longitude'), longitudeRule]"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="dlg-actions px-6 pb-5">
            <v-spacer />
            <v-btn text color="grey darken-1" @click="closeFormDialog">
              Cancel
            </v-btn>
            <v-btn
              rounded
              color="secondary"
              class="px-6"
              type="submit"
              :loading="saving"
              :disabled="!formValid || saving"
            >
              {{ editingId ? 'Save Changes' : 'Add Fitter' }}
              <v-icon right>mdi-check-circle-outline</v-icon>
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Enable/Disable Confirmation Dialog -->
    <v-dialog v-model="statusDialog" max-width="430px" persistent>
      <v-card class="dlg-card">
        <dialog-header
          :title="statusDialogTitle"
          :icon="pendingStatus ? 'mdi-check-circle-outline' : 'mdi-cancel'"
          @close="closeStatusDialog"
        />
        <v-divider />
        <v-card-text class="status-dialog-copy">
          {{ statusDialogMessage }}
        </v-card-text>
        <v-card-actions class="dlg-actions px-6 pb-5">
          <v-spacer />
          <v-btn text color="grey darken-1" @click="closeStatusDialog">
            Cancel
          </v-btn>
          <v-btn
            rounded
            :color="pendingStatus ? 'success' : 'error'"
            :loading="changingStatus"
            @click="changeStatus"
          >
            {{ pendingStatus ? 'Activate' : 'Disable' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DialogHeader from '@/components/DialogHeader'
import windscreenFitterService from '@/services/windscreen-fitter-service'

const emptyForm = () => ({
  name: '',
  phoneNumber: '',
  email: '',
  active: true,
  location: {
    address: '',
    city: '',
    region: '',
    latitude: '',
    longitude: '',
  },
})

const isValidEmail = (value) => {
  if (typeof value !== 'string') return false
  const email = value.trim()
  const atIndex = email.indexOf('@')
  const dotIndex = email.lastIndexOf('.')
  const hasOneAtSign = atIndex === email.lastIndexOf('@')
  const hasWhitespace = Array.from(email).some(
    (character) => character.trim().length === 0
  )

  return (
    atIndex > 0 &&
    hasOneAtSign &&
    dotIndex > atIndex + 1 &&
    dotIndex < email.length - 1 &&
    !hasWhitespace
  )
}

export default {
  name: 'WindscreenFitters',
  components: { DialogHeader },
  data: () => ({
    headers: [
      { text: 'Company Name', value: 'name', width: '22%' },
      {
        text: 'Address',
        value: 'location.address',
        sortable: false,
        width: '20%',
        class: 'hidden-sm-and-down',
        cellClass: 'hidden-sm-and-down',
      },
      {
        text: 'Region',
        value: 'location.region',
        sortable: false,
        width: '12%',
        class: 'hidden-sm-and-down',
        cellClass: 'hidden-sm-and-down',
      },
      {
        text: 'Phone No.',
        value: 'phoneNumber',
        sortable: false,
        width: '15%',
        class: 'hidden-sm-and-down',
        cellClass: 'hidden-sm-and-down',
      },
      {
        text: 'Company Email',
        value: 'email',
        width: '21%',
        class: 'hidden-sm-and-down',
        cellClass: 'hidden-sm-and-down',
      },
      { text: 'Status', value: 'active', sortable: false, width: '10%' },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
        align: 'center',
        width: '110px',
      },
    ],
    fitters: [],
    loadingTable: false,
    saving: false,
    changingStatus: false,
    statusChangingIds: [],
    formDialog: false,
    formValid: false,
    editingId: null,
    form: emptyForm(),
    limit: 10,
    pageSizes: [10, 25, 50, 100],
    pagination: {
      offset: 0,
      returned: 0,
      showingFrom: 0,
      showingTo: 0,
      hasMore: false,
      nextOffset: null,
      previousOffset: null,
    },
    requestSequence: 0,
    statusDialog: false,
    statusTarget: null,
    pendingStatus: false,
    detailDialog: false,
    selectedFitter: null,
    emailRule: (value) =>
      isValidEmail(value) || 'Please provide a valid email address',
    latitudeRule: (value) => {
      const number = Number(value)
      return (
        (Number.isFinite(number) && number >= -90 && number <= 90) ||
        'Location latitude must be between -90 and 90'
      )
    },
    longitudeRule: (value) => {
      const number = Number(value)
      return (
        (Number.isFinite(number) && number >= -180 && number <= 180) ||
        'Location longitude must be between -180 and 180'
      )
    },
  }),
  computed: {
    formTitle() {
      return this.editingId ? 'Edit Windscreen Fitter' : 'Add Windscreen Fitter'
    },
    formSubtitle() {
      return this.editingId
        ? 'Update the provider and location details.'
        : 'Register a new windscreen fitting provider.'
    },
    showingLabel() {
      if (!this.pagination.returned) return 'Showing 0–0'
      return `Showing ${this.pagination.showingFrom}–${this.pagination.showingTo}`
    },
    statusDialogTitle() {
      return this.pendingStatus
        ? 'Activate Windscreen Fitter'
        : 'Disable Windscreen Fitter'
    },
    statusDialogMessage() {
      if (!this.statusTarget) return ''
      const action = this.pendingStatus ? 'activate' : 'disable'
      return `Are you sure you want to ${action} ${this.statusTarget.name}?`
    },
  },
  mounted() {
    this.fetchFitters()
  },
  methods: {
    requiredRule(label) {
      return (value) =>
        (value !== null &&
          value !== undefined &&
          String(value).trim().length > 0) ||
        `${label} is required`
    },
    shortId(id) {
      return id ? id.slice(0, 8) : '—'
    },
    locationValue(item, key) {
      return (item && item.location && item.location[key]) || '—'
    },
    auditActor() {
      const user = this.$store.getters['auth/authenticatedUser'] || {}
      return user.email || user['custom:name'] || user.sub || ''
    },
    notify(message, variant) {
      this.$store.commit('SNACKBAR', {
        state: variant,
        message,
        variant,
      })
    },
    errorMessage(error, fallback) {
      const responseData =
        (error.response && error.response.data) || Object.create(null)
      const responseMessage = responseData.msg || responseData.message
      if (
        typeof responseMessage === 'string' &&
        responseMessage.trim() &&
        responseMessage.trim().toLowerCase() !== 'no message available'
      ) {
        return responseMessage
      }
      if (error.response) return fallback
      return error.message || fallback
    },
    async fetchFitters(offset = this.pagination.offset) {
      const sequence = ++this.requestSequence
      this.loadingTable = true

      try {
        const data = await windscreenFitterService.listWindscreenFitters(
          this.limit,
          offset
        )
        if (sequence !== this.requestSequence) return

        this.fitters = (data.items || []).map((item) => ({
          ...item,
          address: item.location && item.location.address,
        }))
        this.pagination = {
          offset: data.offset,
          returned: data.returned,
          showingFrom: data.showingFrom,
          showingTo: data.showingTo,
          hasMore: data.hasMore,
          nextOffset: data.nextOffset === undefined ? null : data.nextOffset,
          previousOffset:
            data.previousOffset === undefined ? null : data.previousOffset,
        }
      } catch (error) {
        if (sequence !== this.requestSequence) return
        this.notify(
          this.errorMessage(error, 'Failed to load windscreen fitters'),
          'error'
        )
      } finally {
        if (sequence === this.requestSequence) this.loadingTable = false
      }
    },
    goToOffset(offset) {
      if (offset === null || offset === undefined) return
      this.fetchFitters(offset)
    },
    changePageSize() {
      this.fetchFitters(0)
    },
    openCreateDialog() {
      this.editingId = null
      this.form = emptyForm()
      this.formDialog = true
      this.$nextTick(() => {
        if (this.$refs.fitterForm && this.$refs.fitterForm.resetValidation) {
          this.$refs.fitterForm.resetValidation()
        }
      })
    },
    openEditDialog(item) {
      this.editingId = item.id
      this.form = {
        name: item.name,
        phoneNumber: item.phoneNumber,
        email: item.email,
        active: item.active,
        location: {
          address:
            this.locationValue(item, 'address') === '—'
              ? ''
              : item.location.address,
          city:
            this.locationValue(item, 'city') === '—' ? '' : item.location.city,
          region:
            this.locationValue(item, 'region') === '—'
              ? ''
              : item.location.region,
          latitude: item.location ? item.location.latitude : '',
          longitude: item.location ? item.location.longitude : '',
        },
      }
      this.formDialog = true
      this.$nextTick(() => {
        if (this.$refs.fitterForm && this.$refs.fitterForm.resetValidation) {
          this.$refs.fitterForm.resetValidation()
        }
      })
    },
    closeFormDialog() {
      if (this.saving) return
      this.formDialog = false
      this.editingId = null
      this.form = emptyForm()
    },
    buildPayload(actor) {
      return {
        name: this.form.name.trim(),
        phoneNumber: this.form.phoneNumber.trim(),
        email: this.form.email.trim(),
        active: this.form.active,
        location: {
          address: this.form.location.address.trim(),
          city: this.form.location.city.trim(),
          region: this.form.location.region.trim(),
          latitude: Number(this.form.location.latitude),
          longitude: Number(this.form.location.longitude),
        },
        ...(this.editingId ? { updatedBy: actor } : { createdBy: actor }),
      }
    },
    async saveFitter() {
      if (!this.$refs.fitterForm.validate()) return
      const actor = this.auditActor()
      if (!actor) {
        this.notify('Unable to identify the signed-in user', 'error')
        return
      }

      this.saving = true
      const editing = Boolean(this.editingId)

      try {
        if (editing) {
          await windscreenFitterService.updateWindscreenFitter(
            this.editingId,
            this.buildPayload(actor)
          )
        } else {
          await windscreenFitterService.createWindscreenFitter(
            this.buildPayload(actor)
          )
        }

        this.notify(
          editing
            ? 'Windscreen fitter updated successfully'
            : 'Windscreen fitter created successfully',
          'success'
        )
        this.formDialog = false
        const savedId = this.editingId
        this.editingId = null
        this.form = emptyForm()
        await this.fetchFitters(editing ? this.pagination.offset : 0)

        // Keep detail panel sync'ed with updated details
        if (
          editing &&
          this.selectedFitter &&
          this.selectedFitter.id === savedId
        ) {
          const updatedItem = this.fitters.find((item) => item.id === savedId)
          if (updatedItem) {
            this.selectedFitter = updatedItem
          } else {
            this.selectedFitter = null
            this.detailDialog = false
          }
        }
      } catch (error) {
        this.notify(
          this.errorMessage(
            error,
            editing
              ? 'Failed to update windscreen fitter'
              : 'Failed to create windscreen fitter'
          ),
          'error'
        )
      } finally {
        this.saving = false
      }
    },
    openStatusDialog(item, active) {
      this.statusTarget = item
      this.pendingStatus = active
      this.statusDialog = true
    },
    closeStatusDialog() {
      if (this.changingStatus) return
      this.statusDialog = false
      this.statusTarget = null
    },
    async changeStatus() {
      const target = this.statusTarget
      const actor = this.auditActor()
      if (!target || !actor) {
        this.notify('Unable to identify the signed-in user', 'error')
        return
      }

      this.changingStatus = true
      this.statusChangingIds.push(target.id)

      try {
        const updated = this.pendingStatus
          ? await windscreenFitterService.activateWindscreenFitter(
              target.id,
              actor
            )
          : await windscreenFitterService.disableWindscreenFitter(
              target.id,
              actor
            )
        const index = this.fitters.findIndex((item) => item.id === target.id)
        if (index !== -1) {
          const mappedUpdated = {
            ...updated,
            address: updated.location && updated.location.address,
          }
          this.$set(this.fitters, index, mappedUpdated)

          // Sync with opened detail drawer
          if (this.selectedFitter && this.selectedFitter.id === target.id) {
            this.selectedFitter = mappedUpdated
          }
        }
        this.notify(
          `Windscreen fitter ${
            this.pendingStatus ? 'activated' : 'disabled'
          } successfully`,
          'success'
        )
        this.statusDialog = false
        this.statusTarget = null
      } catch (error) {
        this.notify(
          this.errorMessage(error, 'Failed to change windscreen fitter status'),
          'error'
        )
      } finally {
        this.statusChangingIds = this.statusChangingIds.filter(
          (id) => id !== target.id
        )
        this.changingStatus = false
      }
    },
    openDetailPanel(item) {
      this.selectedFitter = item
      this.detailDialog = true
    },
    closeDetailPanel() {
      this.detailDialog = false
      this.selectedFitter = null
    },
    editFromDrawer(item) {
      this.openEditDialog(item)
    },
    statusFromDrawer(item) {
      this.openStatusDialog(item, !item.active)
    },
  },
}
</script>

<style scoped>
.pg-main-card {
  border-radius: 18px;
  border: 1px solid #e8edf5;
  background: #ffffff;
  padding: 18px 20px 20px;
}
.pg-header,
.pg-heading,
.action-cell,
.pg-pagination,
.pagination-size {
  display: flex;
  align-items: center;
}
.pg-header {
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.pg-heading {
  gap: 12px;
}
.pg-badge {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #034ea2 0%, #0a63c4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(3, 78, 162, 0.25);
}
.pg-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f2a55;
  margin: 0;
}
.pg-subtitle {
  color: #6b7684;
  font-size: 13px;
  margin: 2px 0 0;
}
.pg-refresh {
  font-weight: 700;
  text-transform: none;
}
.pg-table {
  border-radius: 12px;
  border: 1px solid #eef2f8;
  overflow: hidden;
}
.pg-table ::v-deep .v-data-table.elevation-1 {
  box-shadow: none !important;
}
.pg-table ::v-deep table {
  table-layout: fixed;
  width: 100%;
}
.pg-table ::v-deep tbody tr {
  cursor: pointer;
}
.pg-table ::v-deep th {
  color: #526173 !important;
  font-weight: 700 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pg-table ::v-deep td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.id-cell {
  color: #526173;
  font-family: monospace;
}
.action-cell {
  justify-content: center;
  gap: 4px;
}
.pg-pagination {
  justify-content: flex-end;
  gap: 14px;
  min-height: 58px;
  padding: 8px 14px;
  border-top: 1px solid #eef2f8;
  color: #4f5968;
  font-size: 12px;
}
.pagination-size {
  gap: 10px;
}
.page-size-select {
  max-width: 72px;
}
.pagination-range {
  min-width: 95px;
  text-align: center;
}
.status-dialog-copy {
  color: #394960;
  font-size: 15px;
  line-height: 1.6;
  padding: 28px 24px 20px !important;
}
.add-button {
  box-shadow: 0 8px 22px rgba(248, 171, 16, 0.35) !important;
}

/* Detail Drawer/Modal Styles */
.detail-text,
.detail-text * {
  font-family: inherit !important;
}
.detail-text {
  font-size: 13px;
  color: #2c3e50;
  line-height: 1.5;
}

/* Common layout utility overrides */
.h-full {
  height: 100% !important;
}
.flex-grow-1 {
  flex-grow: 1 !important;
}
.overflow-y-auto {
  overflow-y: auto !important;
}

@media (max-width: 599px) {
  .pg-main-card {
    padding: 14px 12px 16px;
  }
  .pg-header {
    align-items: flex-start;
  }
  .pg-subtitle,
  .pg-refresh {
    display: none;
  }
  .pg-pagination {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
